import { ACCENT2, BORDER, DARK, DIM, MID } from "../theme";
import { useState } from "react";

// ─── TUTORIAL MODAL ──────────────────────────────────────────────────────────

const QUICKBOOKS_SLIDES = [
  {
    icon: "🌿",
    iconBg: "rgba(45,94,48,0.1)",
    iconBorder: "rgba(45,94,48,0.2)",
    title: "Welcome to Canopy",
    body: "Canopy gives you a clear picture of job-level profitability — which jobs made money, which didn't, and why. It works with QuickBooks or a spreadsheet, and you can add anything they miss: cash costs, payments, and labor. What you add is saved.",
    sub: "Here's a quick walkthrough of what you can do.",
  },
  {
    icon: "📊",
    iconBg: "rgba(45,94,48,0.1)",
    iconBorder: "rgba(45,94,48,0.2)",
    title: "Your dashboard",
    body: "Three cards show profit, revenue and expenses at a glance. Switch between gross and net profit to include fixed costs. Set a revenue goal and watch your progress. The jobs table below lets you sort by margin, costs or profit and open any job.",
    sub: "Use the date range (this month, this quarter, this year, last year, or custom dates) to look at any period. Search for a job or client from the search bar.",
  },
  {
    icon: "📥",
    iconBg: "rgba(140,107,48,0.1)",
    iconBorder: "rgba(140,107,48,0.2)",
    title: "Expenses, labor and revenue",
    body: "Expense Management sorts your QuickBooks expenses into Auto-Matched, Needs Review, Fixed Costs, All Tagged and Tagged in QuickBooks. You can also add a cash cost from there. Inside any job, add labor (hours and rates), other costs, and payments you received outside QuickBooks, such as cash or checks. Everything you enter in Canopy is saved.",
    sub: "Your Data Quality Score rises as you assign expenses to jobs. The higher the score, the more accurate your numbers.",
  },
  {
    icon: "🔗",
    iconBg: "rgba(92,122,90,0.1)",
    iconBorder: "rgba(92,122,90,0.2)",
    title: "QuickBooks connection",
    body: "Canopy reads your QuickBooks Online account through a secure connection. It pulls customers, invoices, sales receipts, purchases and bills. To start, choose Connect QuickBooks on the Dashboard. The first sync runs right after you connect, and after you finish setup QuickBooks syncs automatically about once a day. You can also choose Sync now at any time.",
    sub: "Canopy only reads from QuickBooks and never changes it. You don't need to assign expenses in QuickBooks too, so there's no double work. To disconnect, open Settings (the gear in the sidebar, or More on a phone) and choose Connection.",
  },
  {
    icon: "📝",
    iconBg: "rgba(184,98,42,0.1)",
    iconBorder: "rgba(184,98,42,0.2)",
    title: "Quote generator",
    body: "Build detailed job estimates with itemized cost lines, expected revenue, and margin projections. Save estimates as templates for jobs you do often. When you're ready to share with a client, export a PDF with your business name on it, not Canopy's.",
    sub: "Link an estimate to a real job later to see how your projections compared to actual results.",
  },
  {
    icon: "✨",
    iconBg: "rgba(92,122,90,0.1)",
    iconBorder: "rgba(92,122,90,0.2)",
    title: "Reports and AI Analyst",
    body: "The Reports tab has four pre-built reports — Most Profitable Job Type, Worst Performing Jobs, Monthly Profit Trend, and Client Profitability Ranking — each with a chart, data table, and a plain-English insight. Export to PDF or Excel with one click.",
    sub: "The AI Analyst tab lets you ask questions in plain English — 'which kind of work pays best?', 'why was March rough?', 'who are my best clients?' — and get an answer based on your numbers.",
  },
];

// Businesses that use a spreadsheet instead of QuickBooks: same tour, but slides 3 and 4 describe what they have.
const SPREADSHEET_SLIDES = QUICKBOOKS_SLIDES.map((sl, i) => {
  if (i === 2) return {
    ...sl,
    body: "Open any job to add costs, labor (hours and rates), and payments you received, such as cash or checks. Everything you enter in Canopy is saved, and it counts toward that job's profit right away.",
    sub: "A job with no costs or no labor shows as incomplete instead of a margin, so a missing cost never looks like a great result.",
  };
  if (i === 3) return {
    ...sl,
    title: "Upload Data",
    body: "Choose Upload Data, download the template, fill in your jobs, revenue and expenses, and import it. You can upload again whenever your numbers change.",
    sub: "Uploading the same file again, or an updated version, never counts anything twice. You can delete an upload from the upload history, and anything you entered yourself stays.",
  };
  return sl;
});

function TutorialModal({ onClose, qbConnected, clientType }) {
  const [slide, setSlide] = useState(0);
  const spreadsheet = clientType !== undefined && clientType !== "quickbooks";
  const slides = spreadsheet ? SPREADSHEET_SLIDES : QUICKBOOKS_SLIDES;
  const current = slides[slide];
  const isLast  = slide === slides.length - 1;

  return (
    <div className="tutorial-overlay" onClick={onClose}>
      <div className="tutorial-modal" onClick={e => e.stopPropagation()} style={{ position:"relative" }}>

        {/* Close: phones and tablets show an explicit button (the dimmed area is hard to tap on a small screen) */}
        <button className="tut-close r-close" onClick={onClose} aria-label="Skip the tour" style={{ position:"absolute", top:8, right:8, zIndex:2, background:"none", border:"none", cursor:"pointer", color:DIM, fontSize:24, lineHeight:1 }}>×</button>
        {/* Progress bar */}
        <div className="tutorial-progress">
          {slides.map((_,i) => (
            <div key={i} className="tutorial-progress-dot" style={{ background: i <= slide ? ACCENT2 : BORDER }} onClick={() => setSlide(i)}/>
          ))}
        </div>

        {/* Body */}
        <div className="tutorial-body">
          <div className="tutorial-icon" style={{ background:current.iconBg, border:`1px solid ${current.iconBorder}` }}>
            {current.icon}
          </div>
          <h2 style={{ fontFamily:"'Lora',serif",fontSize:20,fontWeight:500,color:DARK,marginBottom:14,letterSpacing:"-0.01em" }}>
            {current.title}
          </h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:MID,lineHeight:1.75,marginBottom:12 }}>
            {current.body}
          </p>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,lineHeight:1.7,fontStyle:"italic" }}>
            {/* Override slide 3 sub if already connected */}
            {slide === 3 && !spreadsheet && qbConnected
              ? "Your QuickBooks account is already connected. After you finish setup it syncs about once a day, and you can choose Sync now at any time."
              : current.sub}
          </p>
        </div>

        {/* Footer */}
        <div className="tutorial-footer">
          <div style={{ fontSize:11,color:DIM,fontFamily:"'DM Sans',sans-serif" }}>
            {slide + 1} of {slides.length}
          </div>
          <div style={{ display:"flex",gap:8 }}>
            {slide > 0 && (
              <button className="btn" onClick={() => setSlide(s => s - 1)}>← Back</button>
            )}
            {!isLast ? (
              <button className="btn act" onClick={() => setSlide(s => s + 1)}>Next →</button>
            ) : (
              <button className="btn act" onClick={onClose} style={{ background:ACCENT2,borderColor:ACCENT2 }}>
                Get started →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { TutorialModal };
